import { INITIAL_PURSE_NEXT_CHAMPIONSHIP } from "@/utils/constants";

export const prizeCalculatorChampionship = (playersCount: number) => {
  if (playersCount && playersCount <= 0) return [];

  const totalPurse = INITIAL_PURSE_NEXT_CHAMPIONSHIP;
  const sideBetsPct = 0.1;
  const MAX_WINNERS = 30;
  const FIXED_TOP = 5;

  // top-30 prize sum ≈ 250000)
  const top30Prizes = [
    40000, 30000, 25000, 20000, 15000, 12000, 10000, 8000, 7000, 6000, 5508,
    5012, 4561, 4150, 3777, 3437, 3128, 2846, 2590, 2357, 2145, 1952, 1776,
    1616, 1471, 1339, 1218, 1108, 1009, 1000,
  ];

  const scalePrizesToTotal = (
    prizes: number[],
    targetTotal: number,
  ): number[] => {
    const shapeTotal = prizes.reduce((a, b) => a + b, 0);
    if (shapeTotal <= 0 || targetTotal <= 0)
      return new Array(prizes.length).fill(0);

    const MICRO = 1_000_000; // micro-units to reduce rounding drift
    const scaledTarget = targetTotal * MICRO;

    const raw = prizes.map((w) => (w * scaledTarget) / shapeTotal);
    const base = raw.map((r) => Math.round(r / MICRO));

    const used = base.reduce((a, b) => a + b, 0);
    const need = targetTotal - used;

    if (need > 0) {
      const remainders = raw
        .map((r, i) => ({ i, frac: r - Math.round(r / MICRO) * MICRO }))
        .sort((a, b) => b.frac - a.frac || a.i - b.i); // largest remainder, higher rank first
      for (let k = 0; k < need; k++)
        base[remainders[k % remainders.length].i] += 1;
    }
    return base;
  };

  // main prize pool after side bets
  const prizePool = Math.round(totalPurse * (1 - sideBetsPct));

  // canonical top-30 winnings for this event
  const fullTop30Winnings = scalePrizesToTotal(top30Prizes, prizePool);

  const paidCount = Math.min(playersCount, MAX_WINNERS);
  const prizeList = new Array(playersCount).fill(0);

  // case 1: 30+ players — just pay the canonical top 30
  if (paidCount >= MAX_WINNERS) {
    for (let i = 0; i < MAX_WINNERS; i++) prizeList[i] = fullTop30Winnings[i];
    return prizeList;
  }

  // case 2: ≤5 players — give the entire pool to top N using the head of the shape
  if (paidCount <= FIXED_TOP) {
    const headOnly = scalePrizesToTotal(
      top30Prizes.slice(0, paidCount),
      prizePool,
    );
    for (let i = 0; i < paidCount; i++) prizeList[i] = headOnly[i];
    return prizeList;
  }

  // case 3: 6..29 players — lock top-5, redistribute the 6..30 tail into 6..N
  for (let i = 0; i < FIXED_TOP; i++) prizeList[i] = fullTop30Winnings[i];

  const tailPrizePool = fullTop30Winnings
    .slice(FIXED_TOP)
    .reduce((a, b) => a + b, 0); // sum ranks 6..30
  const tailShape = top30Prizes.slice(FIXED_TOP, paidCount); // prizes for 6..N
  const redistributedTail = scalePrizesToTotal(tailShape, tailPrizePool);

  for (let i = 0; i < redistributedTail.length; i++) {
    prizeList[FIXED_TOP + i] = redistributedTail[i];
  }

  return prizeList;
};
